import { del, get, patch, post, put } from './http';
import type {
  Admin,
  AdminRole,
  AdminSession,
  AuditEvent,
  CreditGrant,
  CreditOrderRow,
  CreditOrderStatus,
  CreditPlan,
  CrmNote,
  CrmProfile,
  FollowUp,
  LifecycleStage,
  LoginStep,
  NoteKind,
  Page,
  PlatformDashboard,
  StoreCredit,
  StoreDetail,
  StoreRow,
  StoreUser,
  Supplier,
  SupplierCategory,
  SupplierOffer,
} from './types';

/** Every admin endpoint, one function each. Paths are relative to /api/v1/admin. */
export const adminApi = {
  // sign-in
  login: (email: string, password: string) => post<LoginStep>('/auth/login', { email, password }),
  code: (challenge: string, code: string) => post<AdminSession>('/auth/code', { challenge, code }),
  me: () => get<Admin>('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<Admin>('/auth/password', { currentPassword, newPassword }),

  dashboard: () => get<PlatformDashboard>('/dashboard'),

  // stores
  stores: (query: StoreQuery) => get<Page<StoreRow>>('/stores', { ...query }),
  tags: () => get<string[]>('/stores/tags'),
  store: (id: number) => get<StoreDetail>(`/stores/${id}`),
  setStoreStatus: (id: number, active: boolean, reason?: string) =>
    patch<StoreDetail>(`/stores/${id}/status`, { active, reason }),
  storeUsers: (id: number) => get<StoreUser[]>(`/stores/${id}/users`),
  setUserStatus: (storeId: number, userId: number, active: boolean) =>
    patch<StoreUser[]>(`/stores/${storeId}/users/${userId}`, { active }),
  updateProfile: (id: number, body: { lifecycleStage?: LifecycleStage; assignedAdminId?: number; unassign?: boolean; tags?: string[] }) =>
    put<CrmProfile>(`/stores/${id}/profile`, body),

  // relationship
  notes: (storeId: number) => get<CrmNote[]>(`/stores/${storeId}/notes`),
  addNote: (storeId: number, body: { kind: NoteKind; body: string; pinned?: boolean }) =>
    post<CrmNote>(`/stores/${storeId}/notes`, body),
  pinNote: (storeId: number, noteId: number, pinned: boolean) =>
    patch<CrmNote>(`/stores/${storeId}/notes/${noteId}`, { pinned }),
  deleteNote: (storeId: number, noteId: number) => del(`/stores/${storeId}/notes/${noteId}`),
  storeFollowUps: (storeId: number) => get<Page<FollowUp>>(`/stores/${storeId}/follow-ups`),
  addFollowUp: (storeId: number, body: { title: string; dueAt: string; assignedAdminId?: number }) =>
    post<FollowUp>(`/stores/${storeId}/follow-ups`, body),
  followUps: (query: { scope: 'mine' | 'all'; status?: string; page?: number; size?: number }) =>
    get<Page<FollowUp>>('/follow-ups', query),
  updateFollowUp: (id: number, body: { title?: string; dueAt?: string; assignedAdminId?: number; done?: boolean }) =>
    patch<FollowUp>(`/follow-ups/${id}`, body),
  deleteFollowUp: (id: number) => del(`/follow-ups/${id}`),

  // credit
  storeCredit: (storeId: number) => get<StoreCredit>(`/stores/${storeId}/credit`),
  grant: (storeId: number, messages: number, reason: string) =>
    post<CreditGrant>(`/stores/${storeId}/credit/grants`, { messages, reason }),
  orders: (query: { status?: CreditOrderStatus; storeId?: number; page?: number; size?: number }) =>
    get<Page<CreditOrderRow>>('/credit-orders', query),
  approve: (id: number, note?: string) => post<CreditOrderRow>(`/credit-orders/${id}/approve`, { note }),
  reject: (id: number, note?: string) => post<CreditOrderRow>(`/credit-orders/${id}/reject`, { note }),
  plans: () => get<CreditPlan[]>('/credit-plans'),
  savePlan: (plan: Omit<CreditPlan, 'id'> & { id?: number }) =>
    plan.id ? put<CreditPlan>(`/credit-plans/${plan.id}`, plan) : post<CreditPlan>('/credit-plans', plan),
  grants: (query: { storeId?: number; page?: number; size?: number }) => get<Page<CreditGrant>>('/credit-grants', query),

  // suppliers
  categories: () => get<SupplierCategory[]>('/suppliers/categories'),
  saveCategory: (body: Partial<SupplierCategory> & { id?: number }) =>
    body.id ? put<SupplierCategory>(`/suppliers/categories/${body.id}`, body) : post<SupplierCategory>('/suppliers/categories', body),
  suppliers: () => get<Supplier[]>('/suppliers'),
  saveSupplier: (body: Partial<Supplier> & { id?: number }) =>
    body.id ? put<Supplier>(`/suppliers/${body.id}`, body) : post<Supplier>('/suppliers', body),
  offers: () => get<SupplierOffer[]>('/suppliers/offers'),
  saveOffer: (body: Partial<SupplierOffer> & { id?: number }) =>
    body.id ? put<SupplierOffer>(`/suppliers/offers/${body.id}`, body) : post<SupplierOffer>('/suppliers/offers', body),

  // audit
  audit: (query: AuditQuery) => get<Page<AuditEvent>>('/audit', { ...query }),
  storeAudit: (storeId: number, query: AuditQuery) => get<Page<AuditEvent>>(`/stores/${storeId}/audit`, { ...query }),

  // team
  admins: () => get<Admin[]>('/admins'),
  createAdmin: (body: { email: string; fullName: string; role: AdminRole; temporaryPassword: string }) =>
    post<Admin>('/admins', body),
  updateAdmin: (id: number, body: { fullName?: string; role?: AdminRole; active?: boolean }) =>
    patch<Admin>(`/admins/${id}`, body),
  resetTwoFactor: (id: number) => post<Admin>(`/admins/${id}/reset-2fa`),
  resetPassword: (id: number, temporaryPassword: string) =>
    post<Admin>(`/admins/${id}/reset-password`, { temporaryPassword }),
};

export interface StoreQuery {
  q?: string;
  stage?: LifecycleStage;
  tag?: string;
  status?: 'active' | 'suspended';
  assignedTo?: number;
  sort?: 'newest' | 'oldest' | 'name' | 'activity' | 'credit' | 'customers';
  page?: number;
  size?: number;
}

export interface AuditQuery {
  storeId?: number;
  actorType?: string;
  action?: string;
  entityType?: string;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

/** Query keys, hierarchical so one mutation can invalidate everything it changed. */
export const keys = {
  dashboard: ['dashboard'] as const,
  stores: ['stores'] as const,
  storeList: (query: StoreQuery) => ['stores', 'list', query] as const,
  store: (id: number) => ['stores', id] as const,
  storePart: (id: number, part: string) => ['stores', id, part] as const,
  tags: ['stores', 'tags'] as const,
  followUps: ['follow-ups'] as const,
  orders: ['orders'] as const,
  plans: ['plans'] as const,
  grants: ['grants'] as const,
  suppliers: ['suppliers'] as const,
  audit: ['audit'] as const,
  admins: ['admins'] as const,
};
